import {
  VStack,
  Box,
  Text,
  Divider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  SimpleGrid,
} from "@chakra-ui/react";
import { useMemo, useRef } from "react";
import { Formik } from "formik";
import { useRouter } from "next/router";
import * as Yup from "yup";
import { useSelector, useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import SearchFormHeader from "../../practitioner-search/SearchFormHeader";
import { decrementPmsSearchStep } from "../../redux/actions/layout";
import FooterPmsSearchPayment from "../../navigation/footer/FooterPmsSearchPayment";
import { postData } from "../../utilities/axios";
import PMSNavButtons from "./PMSNavButtons";
import {
  renderAvailabilityContent,
  sectionStyleEditing2,
} from "../../utilities/render-helpers/renderPractitionerProfile";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { resetPms } from "../../redux/actions/pmsSearch";

const PmsSearchPaymentForm = (props) => {
  const pmsSearch = useSelector((state) => state.pmsSearch);
  const dispatch = useDispatch();
  const router = useRouter();
  const formPayment = useRef();
  const token = useSelector((state) => state.user.JWTToken);
  const [loading, setLoading] = useState(false);

  const { availability: availabilityLabels } = props.utilData;

  // pmsInfo
  const [yourDetails, setYourDetails] = useState([]);
  const [practitionerPreferences, setPractitionerPreferences] = useState([]);
  const [sessionDetails, setSessionDetails] = useState([]);
  const [medicalDetails, setMedicalDetails] = useState([]);
  const availability = useMemo(
    () =>
      pmsSearch.availability || [
        [false, false, false, false, false, false, false],
        [false, false, false, false, false, false, false],
        [false, false, false, false, false, false, false],
        [false, false, false, false, false, false, false],
        [false, false, false, true, false, false, false],
      ],
    [pmsSearch.availability]
  );

  //integrate form with redux
  const [hasLoaded, setHasLoaded] = useState(false);
  useEffect(() => {
    setHasLoaded(true);
    generateYourDetails();
    generatePractitionerPreferences();
    generateSessionDetails();
    generateMedicalDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pmsSearch]);

  const renderStr = (data) => {
    return data || "None given";
  };

  const renderArr = (arr) => {
    if (!arr?.length) return "Non given";
    let newArr = arr?.filter((elem) => elem.selected).map((elem) => elem.label);

    return newArr.length ? newArr.join(",") : "None given";
  };

  const generateYourDetails = () => {
    setYourDetails([
      "Name",
      pmsSearch.fullname,
      "Contact email",
      pmsSearch.email,
      "Contact phone",
      renderStr(pmsSearch.phone),
      "Your age",
      renderStr(pmsSearch.age),
      "Your occupation",
      renderStr(pmsSearch.occupation),
      "Your gender",
      renderArr(pmsSearch.genderIdentification),
    ]);
  };

  const generatePractitionerPreferences = () => {
    setPractitionerPreferences([
      "Practitioner gender",
      renderArr(pmsSearch.womanOrMan),
      "LGBTQ+ affirmative",
      renderArr(pmsSearch.lgbtq),
      "Practitioner religion",
      renderArr(pmsSearch.religion),
      "Practitioner culture",
      renderStr(pmsSearch.culture),
      "Spoken language(s) in addition to English",
      renderArr(pmsSearch.languages),
    ]);
  };

  const generateSessionDetails = () => {
    setSessionDetails([
      "Session preference",
      renderArr(pmsSearch.sessionTypes),
      "Disabled access needed?",
      renderArr(pmsSearch.disabledAccess),
      "Home location",
      renderStr(pmsSearch.homeAddress),
      "Office location",
      renderStr(pmsSearch.officeAddress),
      "Other location",
      renderStr(pmsSearch.otherAddress),
      "Approximate budget per session",
      `£${pmsSearch.minBudget} - £${pmsSearch.maxBudget}`,
    ]);
  };

  const generateMedicalDetails = () => {
    setMedicalDetails([
      "Pay with health insurer?",
      renderArr(pmsSearch.healthInsurers),
      "Are you taking medication?",
      renderStr(pmsSearch.medication),
      "Have you been referred by a health professional",
      renderArr(pmsSearch.referred),
      "Previous therapy or coaching experience?",
      renderArr(pmsSearch.referredForThis),
      "Notes on previous therapy or coaching",
      renderStr(pmsSearch.previousExperience),
    ]);
  };

  // Footer navigation
  const handleClickPrev = () => {
    // Update state by forcing form submit
    dispatch(decrementPmsSearchStep());
  };

  const handleClickNext = () => {
    //setDirection('next');
    formPayment.current.handleSubmit();
  };

  const renderAccordianItem = (title, arrInfo) => {
    return (
      <AccordionItem>
        <h2>
          <AccordionButton _expanded={{ bg: "brand.400", color: "white" }}>
            <Box as="span" flex="1" textAlign="left">
              {title}
            </Box>
            <AccordionIcon />
          </AccordionButton>
        </h2>
        <AccordionPanel pb={4}>
          {title === "Your symptoms" ? (
            <SimpleGrid columns={1} spacing={4}>
              <Box>
                <Text
                  textStyle="h7"
                  color="brand.200"
                  style={{ whiteSpace: "pre-wrap" }}
                >
                  {arrInfo[0]}
                </Text>
              </Box>
            </SimpleGrid>
          ) : title === "Preferred availability" ? (
            <Box sx={sectionStyleEditing2}>
              <Box position="relative">
                <VStack alignItems="flex-start">
                  <Box w="100%">
                    {renderAvailabilityContent(
                      availabilityLabels,
                      availability,
                      () => null
                    )}
                  </Box>
                </VStack>
              </Box>
            </Box>
          ) : (
            <SimpleGrid columns={2} spacing={4}>
              {arrInfo.map((item, idx) =>
                idx % 2 === 0 ? (
                  <Box key={idx}>
                    <Text textStyle="h7" color="brand.300">
                      {item}
                    </Text>
                  </Box>
                ) : (
                  <Box key={idx} display="flex" justifyContent="flex-end">
                    <Text
                      textStyle="h7"
                      color="brand.200"
                      style={{ whiteSpace: "pre-wrap" }}
                    >
                      {item}
                    </Text>
                  </Box>
                )
              )}
            </SimpleGrid>
          )}
        </AccordionPanel>
      </AccordionItem>
    );
  };

  // Wait for state to load
  if (!hasLoaded) return null;

  // Render
  return (
    <Formik
      innerRef={formPayment}
      initialValues={{}}
      validationSchema={Yup.object({})}
      onSubmit={() => {
        let req = { formData: { ...pmsSearch }, contactEmail: pmsSearch.email };
        setLoading(true);
        try {
          postData(token, req, `${process.env.NEXT_PUBLIC_NEW_PMS_URL}`).then(
            (data) => {
              let req = {
                pmsId: data.pmsId,
                redirectUrl: `${process.env.NEXT_PUBLIC_HOST_URL}/accounts/user-account/dashboard`,
              };
              try {
                postData(
                  token,
                  req,
                  `${process.env.NEXT_PUBLIC_NEW_PAYMENT_URL}/pms/payment`
                ).then((data) => {
                  setLoading(false);
                  dispatch(resetPms());
                  router.push(data.url);
                });
              } catch (error) {
                setLoading(false);
                console.error("Error paying PMS", error);
              }
            }
          );
        } catch (error) {
          setLoading(false);
          console.error("Error posting PMS details", error);
        }
      }}
    >
      {(formik) => (
        <>
          <Box width="100%" maxW="container.sm" mx="auto" mb="120px" pt="24px">
            <SearchFormHeader />
            <VStack
              as="form"
              maxW="full"
              py={7}
              px={5}
              //mx="auto"
              spacing={2}
              alignItems="flex-start"
              bg="white"
              borderRadius={{ base: 0, md: 20 }}
              onSubmit={formik.handleSubmit}
              boxShadow="lg"
              overflow="hidden"
            >
              <Text textStyle="h6">
                Please review your information below and then click on
                &apos;Continue to payment&apos; to submit your request to
                Welldoing. One of our matching specialists will review your
                details and contact you with a match in the next 2-3 working
                days.
              </Text>
              <p></p>
              <Divider />
              <Accordion width="100%" defaultIndex={[0]} allowMultiple>
                {renderAccordianItem("Your symptoms", [
                  pmsSearch.symptoms || "None given",
                ])}
                {renderAccordianItem("Your details", yourDetails)}
                {renderAccordianItem(
                  "Practitioner preferences",
                  practitionerPreferences
                )}
                {renderAccordianItem("Session preferences", sessionDetails)}
                {renderAccordianItem("Preferred availability", availability)}
                {renderAccordianItem("Medical details", medicalDetails)}
              </Accordion>

              <PMSNavButtons
                handleClickNext={handleClickNext}
                handleClickPrev={handleClickPrev}
                fwdLabel="Continue to payment"
              />
              <FooterPmsSearchPayment
                handleClickNext={handleClickNext}
                handleClickPrev={handleClickPrev}
              />
            </VStack>
          </Box>
          {/* Footer */}
          {loading && <LoadingSpinner message="One moment..." />}
        </>
      )}
    </Formik>
  );
};

export default PmsSearchPaymentForm;
